import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
// Import directly from sub-file to avoid circular dependency through investment-holdings-v2 barrel
import * as investmentHoldingsV2Selectors from 'app-components/flux/investment-holdings-v2/investmentHoldingsV2Selectors';
import { securitiesSelectors } from 'app-components/flux/securities';
import type RootState from 'app-components/utils/redux-store/rootState';

import { investmentsQuotesDetailedApi } from './investmentsQuotesDetailedApi';
import {
  type DetailedQuote,
  type GetDetailedQuotesRequest,
  INVESTMENTS_QUOTES_DETAILED_REDUCER_KEY,
  type SymbolInput,
} from './investmentsQuotesDetailedTypes';
import { keyFromSecurity, quoteTypeFromSecurityType } from './investmentsQuotesDetailedUtils';

export const getDetailedQuotes = createAsyncThunk<DetailedQuote[], GetDetailedQuotesRequest>(
  `${INVESTMENTS_QUOTES_DETAILED_REDUCER_KEY}/getDetailedQuotes`,
  (params) => investmentsQuotesDetailedApi.getDetailedQuotes(params),
);

export const getAllDetailedQuotes = createAsyncThunk<DetailedQuote[]>(
  `${INVESTMENTS_QUOTES_DETAILED_REDUCER_KEY}/getAllDetailedQuotes`,
  (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const investmentsDetailedQuotesRelease = featureFlagsSelectors.getFeatureFlag(
      state,
      'investmentsDetailedQuotesRelease',
    );
    if (investmentsDetailedQuotesRelease) {
      const holdings = investmentHoldingsV2Selectors.getResourcesById(state);
      const securities = securitiesSelectors.getResourcesById(state);

      const symbols = Object.values(
        holdings.reduce<Record<string, SymbolInput>>((results, holding) => {
          const security = securities.get(holding.localSecurityId || '');
          if (security?.symbol) {
            const key = keyFromSecurity(security) || '';
            if (key) {
              results[key] = {
                clientId: uuidv4(),
                symbol: security.symbol || '',
                securityType: quoteTypeFromSecurityType(security.securityType || 'OTHER'),
                currency: 'USD',
              };
            }
          }
          return results;
        }, {}),
      );

      if (symbols.length) {
        return dispatch(getDetailedQuotes({ symbols })).unwrap();
      }
    }

    return [];
  },
);
